/**
 * PV Quiz Viking - Offer Bridge (Screen 14)
 *
 * Connects the REAL, unmodified pv-product-section (same component the live
 * PDP uses) to the quiz's own state machine, without ever editing
 * pv-product-section.liquid/.js:
 *
 * 1. Injects the personalized subtitle/paragraph/bullets into the section's
 *    own "benefits" block (static fallback first, then replaced by JS - same
 *    graceful-degradation pattern as the rest of the quiz).
 * 2. Intercepts the real add-to-cart form submit in the CAPTURE phase (which
 *    always runs before pv-product-section.js's own bubble-phase listener on
 *    the same form) and redirects into the quiz flow instead of adding to
 *    cart here. Scoped to this page's own section instance only - never
 *    touches the real PDP's form.
 * 3. Injects the "recommended add-on" card (Libido Fire Drops) right after
 *    the section's own FAQ blocks, always shown (unlike the conditional
 *    Screen 16 upsell). The classic card only sets meta.addonAccepted, which
 *    checkout turns into a cart line. The "boost" card adds it straight to
 *    the real cart instead (button keeps its "Add to order" copy) and only
 *    records meta.addonInCartVariantId - checkout keeps that cart line as-is
 *    rather than pushing the add-on a second time.
 */

(function () {
  'use strict';

  var ANSWERS_KEY = 'pv_quiz_viking_answers';
  var SCOPE_SELECTOR = '[id*="__q13_product"]';

  function readJSON(key, fallback) {
    try {
      var raw = sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) : fallback;
    } catch (e) {
      return fallback;
    }
  }

  // "Based On Your Answers..." doesn't apply if the user skipped straight to
  // the offer without answering anything - hide it rather than claim a
  // recommendation that isn't actually based on anything.
  function updateBannerVisibility(answers) {
    var banner = document.querySelector('.pv-quiz-offer-header__banner');
    if (!banner) return;
    banner.hidden = Object.keys(answers).length === 0;
  }

  function initPersonalization() {
    var answers = readJSON(ANSWERS_KEY, {});
    updateBannerVisibility(answers);

    var scope = document.querySelector(SCOPE_SELECTOR);
    var container = scope && scope.querySelector('.pv-product__benefits');
    var P = window.PVQuizVikingPersonalization;
    if (!container || !P) return;

    var subtitle = P.getSubtitle(answers.q1);
    var paragraph = P.assembleParagraph(answers);
    var bullets = P.getBenefitBullets(answers.q1);

    var bulletsHtml = bullets
      .map(function (text) {
        return '<li>' + text + '</li>';
      })
      .join('');

    // Opt-out per template via the offer header's "hide_dynamic_subtitle" setting.
    var hideSubtitle = !!document.querySelector('[data-hide-dynamic-subtitle]');

    container.innerHTML =
      (hideSubtitle ? '' : '<p class="pv-quiz-offer-header__dynamic-subtitle">' + subtitle + '</p>') +
      '<p class="pv-quiz-offer-header__paragraph">' +
      paragraph +
      '</p><ul>' +
      bulletsHtml +
      '</ul>';
  }

  // Re-run on every visit to the offer step (not just page load) so this
  // stays in sync if the user goes back and changes an answer, then returns.
  function initPersonalizationOnStep14() {
    window.addEventListener('hashchange', function () {
      if (window.location.hash === '#step-14') initPersonalization();
    });
  }

  function initAddonCard() {
    var scope = document.querySelector(SCOPE_SELECTOR);
    var dataEl = document.getElementById('pv-quiz-offer-addon-data');
    if (!scope || !dataEl) {
      if (window.console) console.warn('PV Quiz Viking: addon card skipped - scope or data script not found', scope, dataEl);
      return;
    }

    var data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      if (window.console) console.warn('PV Quiz Viking: addon card skipped - bad JSON', dataEl.textContent, e);
      return;
    }
    if (!data.variantId) {
      if (window.console) console.warn('PV Quiz Viking: addon card skipped - no variantId', data);
      return;
    }

    // Insertion point: right after the last FAQ block if present, otherwise
    // append to the end of the blocks column, otherwise the section itself -
    // never silently skip just because the FAQ blocks moved/changed.
    var faqItems = scope.querySelectorAll('.pv-product__faq');
    var lastFaq = faqItems.length ? faqItems[faqItems.length - 1] : null;
    var blocksColumn = scope.querySelector('.pv-product__blocks');
    if (!lastFaq && !blocksColumn) {
      if (window.console) console.warn('PV Quiz Viking: addon card skipped - no insertion point found in', scope);
      return;
    }

    var app = window.PVQuizVikingApp;
    var alreadyAdded = !!(app && app.meta && app.meta.addonAccepted);

    var hasAddonDiscount = data.comparePrice && data.comparePrice !== data.price;
    var addonPriceHtml =
      '<p class="pv-quiz-offer-addon__price">' +
      (hasAddonDiscount
        ? '<span class="pv-quiz-offer-addon__compare">' + data.comparePrice + '</span>'
        : '') +
      '<span class="pv-quiz-offer-addon__current">' +
      data.price +
      '</span>' +
      '</p>';

    var card = document.createElement('div');
    var ctaLabel = alreadyAdded ? 'Remove' : 'Add to Order';

    // "Boost" style (Figma 6528:1291 desktop / 6532:5686 mobile), picked per
    // template via the offer header's "addon_style" setting. Same toggle
    // button/data attributes as the classic card so the click handler below
    // and the checkout flow don't care which one rendered.
    if (data.style === 'boost') {
      var b = data.boost || {};
      var pills = [b.pill1, b.pill2].filter(Boolean);
      var checkSvg =
        '<svg class="pv-quiz-offer-addon__check" viewBox="0 0 16 16" aria-hidden="true">' +
        '<circle cx="8" cy="8" r="7.5" fill="none" stroke="#5C7A3F"/>' +
        '<path d="M4.5 8.3l2.4 2.4 4.6-5" fill="none" stroke="#5C7A3F" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>';

      card.className = 'pv-quiz-offer-addon pv-quiz-offer-addon--boost';
      card.innerHTML =
        (b.topBadge ? '<span class="pv-quiz-offer-addon__top-badge">' + b.topBadge + '</span>' : '') +
        '<div class="pv-quiz-offer-addon__row">' +
        (data.image
          ? '<div class="pv-quiz-offer-addon__media"><img class="pv-quiz-offer-addon__image" src="' +
            data.image +
            '" alt="' +
            data.title +
            '" loading="lazy"></div>'
          : '') +
        '<div class="pv-quiz-offer-addon__copy">' +
        (pills.length
          ? '<div class="pv-quiz-offer-addon__pills">' +
            pills.map(function (t) { return '<span class="pv-quiz-offer-addon__pill">' + t + '</span>'; }).join('') +
            '</div>'
          : '') +
        '<p class="pv-quiz-offer-addon__title">' + data.title + '</p>' +
        (b.benefit ? '<p class="pv-quiz-offer-addon__benefit">' + checkSvg + '<span>' + b.benefit + '</span></p>' : '') +
        '<div class="pv-quiz-offer-addon__pricing">' +
        addonPriceHtml +
        (b.priceNote ? '<p class="pv-quiz-offer-addon__price-note">' + b.priceNote + '</p>' : '') +
        '</div>' +
        '</div>' +
        '</div>' +
        '<button type="button" class="pv-quiz-offer-addon__cta pv-quiz-offer-addon__cta--boost" data-action="offer-addon-toggle" data-variant-id="' +
        data.variantId +
        '" data-selling-plan-id="' +
        (data.sellingPlanId || '') +
        '" data-label-add="' + (b.cta || 'Add to order') + '">' +
        (b.cta || 'Add to order') +
        '</button>';
    } else {
    card.className = 'pv-quiz-offer-addon';
    card.innerHTML =
      '<div class="pv-quiz-offer-addon__row">' +
      (data.image
        ? '<img class="pv-quiz-offer-addon__image" src="' +
          data.image +
          '" alt="' +
          data.title +
          '" width="227" height="162" loading="lazy">'
        : '') +
      '<div class="pv-quiz-offer-addon__copy">' +
      '<p class="pv-quiz-offer-addon__eyebrow">Recommended from your answers</p>' +
      '<div class="pv-quiz-offer-addon__copy-text">' +
      '<p class="pv-quiz-offer-addon__title">' +
      'Libido Fire Drops' +
      '</p>' +
      addonPriceHtml +
      '</div>' +
      '<button type="button" class="pv-btn pv-btn--outline pv-quiz-offer-addon__cta" data-action="offer-addon-toggle" data-variant-id="' +
      data.variantId +
      '" data-selling-plan-id="' +
      (data.sellingPlanId || '') +
      '">' +
      ctaLabel +
      '</button>' +
      '</div>' +
      '</div>';
    }

    if (lastFaq) {
      lastFaq.insertAdjacentElement('afterend', card);
    } else {
      blocksColumn.appendChild(card);
    }

    card.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-action="offer-addon-toggle"]');
      if (!btn) return;
      if (!app) return;

      // Boost card: straight into the real cart, button keeps its original
      // "Add to order" copy. Never sets addonAccepted, so checkout doesn't
      // push the add-on again on top of this cart line.
      if (data.style === 'boost') {
        if (btn.disabled) return;
        btn.disabled = true;
        addAddonToCart(btn)
          .then(function () {
            setBoostAddonMeta(app, true, btn, data);
          })
          .catch(function (err) {
            if (window.console) console.error('PV Quiz Viking: addon cart error', err);
          })
          .then(function () {
            btn.disabled = false;
          });
        return;
      }

      var isAdded = !!(app.meta && app.meta.addonAccepted);
      setAddonMeta(app, !isAdded, btn, data);
      btn.textContent = isAdded ? 'Add to Order' : 'Remove';
    });

    if (data.style === 'boost' && app) {
      // Earlier builds of this card also set addonAccepted for the boost
      // add-on - drop that leftover so checkout doesn't add it a second time.
      if (app.meta && app.meta.addonAccepted && String(app.meta.addonVariantId) === String(data.variantId)) {
        setAddonMeta(app, false, null, data, true);
      }
      watchCartForAddon(app, card.querySelector('[data-action="offer-addon-toggle"]'), data);
    }
  }

  // Keeps meta.addonInCartVariantId in step with the real cart (line removed
  // from the mini cart, cart emptied elsewhere, ...) so the Screen 16 upsell,
  // order summary and checkout all agree on whether it's there.
  function watchCartForAddon(app, btn, data) {
    if (!btn) return;
    var timer = null;

    function resync() {
      if (btn.disabled) return; // our own add is in flight
      fetchCart()
        .then(function (cart) {
          if (btn.disabled) return;
          var line = findAddonLine(cart, btn);
          var tracked = !!(app.meta && app.meta.addonInCartVariantId);
          if (!!line === tracked) return;
          setBoostAddonMeta(app, !!line, btn, data);
        })
        .catch(function () { /* keep current state */ });
    }

    function schedule() {
      clearTimeout(timer);
      timer = setTimeout(resync, 250);
    }

    var drawer = qsDrawer();
    if (drawer && window.MutationObserver) {
      new MutationObserver(schedule).observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['open'] });
    }
    document.addEventListener('cart:updated', schedule);
    resync();
  }

  function setAddonMeta(app, accepted, btn, data, silent) {
    if (!accepted) {
      app.setMeta({
        addonAccepted: false,
        addonVariantId: null,
        addonSellingPlanId: null,
        addonSellingPlans: null,
        addonTitle: null,
        addonPrice: null,
        addonComparePrice: null
      });
      if (!silent) app.trackEvent('quiz_offer_addon_removed', { product: 'libido_fire_drops' });
      return;
    }

    app.setMeta({
      addonAccepted: true,
      addonVariantId: btn.dataset.variantId,
      addonSellingPlanId: btn.dataset.sellingPlanId || null,

      addonSellingPlans: Array.isArray(data.sellingPlans) ? data.sellingPlans : null,
      addonTitle: data.title,
      addonPrice: data.price,
      addonComparePrice: data.comparePrice
    });
    app.trackEvent('quiz_offer_addon_accepted', { product: 'libido_fire_drops' });
  }

  // Boost add-on: the cart line is the source of truth. Title/price are only
  // kept for the Screen 15 order summary; addonAccepted stays untouched.
  function setBoostAddonMeta(app, inCart, btn, data) {
    if (!inCart) {
      app.setMeta({
        addonInCartVariantId: null,
        addonSellingPlanId: null,
        addonTitle: null,
        addonPrice: null,
        addonComparePrice: null
      });
      return;
    }

    var wasTracked = !!(app.meta && app.meta.addonInCartVariantId);
    app.setMeta({
      addonInCartVariantId: btn.dataset.variantId,
      addonSellingPlanId: btn.dataset.sellingPlanId || null,
      addonTitle: data.title,
      addonPrice: data.price,
      addonComparePrice: data.comparePrice
    });
    if (!wasTracked) app.trackEvent('quiz_offer_addon_accepted', { product: 'libido_fire_drops' });
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  function findAddonLine(cart, btn) {
    var line = cart && (cart.items || []).filter(function (item) {
      return String(item.variant_id) === String(btn.dataset.variantId);
    })[0];
    if (line) btn.dataset.lineKey = line.key;
    else delete btn.dataset.lineKey;
    return line || null;
  }

  function qsDrawer() {
    return document.querySelector('cart-drawer#mini-cart') || document.querySelector('cart-drawer[section="mini-cart"]');
  }

  function getMiniCartSectionId() {
    var drawer = qsDrawer();
    return (drawer && drawer.getAttribute('section')) || 'mini-cart';
  }

  function normalizeMiniCartSections(sections) {
    if (!sections || sections['mini-cart']) return sections;
    var keys = Object.keys(sections);
    if (keys.length !== 1) return sections;
    return { 'mini-cart': sections[keys[0]] };
  }

  function refreshMiniCart(cart, sections, open) {
    var drawer = qsDrawer();
    var detail = { openMiniCart: open };
    if (cart) {
      detail.cart = Object.assign({}, cart);
      if (sections) detail.cart.sections = normalizeMiniCartSections(sections);
    }

    try {
      var ev = new CustomEvent('cart:refresh', { bubbles: true, detail: detail });
      if (drawer) {
        drawer.dispatchEvent(ev);
      } else {
        document.documentElement.dispatchEvent(ev);
      }
      document.dispatchEvent(ev);
      window.dispatchEvent(ev);
    } catch (e) { /* noop */ }

    if (open && drawer) {
      try { drawer.open = true; } catch (e) { /* noop */ }
      drawer.setAttribute('open', '');
    }
  }


  // Repeat clicks just reopen the mini cart instead of stacking another unit
  // (quantity can still be changed from the mini cart itself).
  function addAddonToCart(btn) {
    return fetchCart()
      .catch(function () { return null; })
      .then(function (cart) {
        if (findAddonLine(cart, btn)) {
          refreshMiniCart(cart, null, true);
          return null;
        }

        var item = { id: parseInt(btn.dataset.variantId, 10), quantity: 1 };
        if (btn.dataset.sellingPlanId) item.selling_plan = parseInt(btn.dataset.sellingPlanId, 10);

        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [item], sections: getMiniCartSectionId(), sections_url: window.location.pathname })
        })
          .then(function (response) {
            return response.json().then(function (body) {
              if (!response.ok) throw new Error((body && body.description) || 'Cart request failed');
              return body;
            });
          })
          .then(function (body) {
            var line = body && body.items && body.items[0];
            if (line && line.key) btn.dataset.lineKey = line.key;

            // /cart/add.js only returns the added line - item_count for the header
            // bubble has to come from /cart.js.
            return fetchCart()
              .catch(function () { return null; })
              .then(function (fresh) {
                refreshMiniCart(fresh, body && body.sections, true);
                return body;
              });
          });
      });
  }

  function initSubmitBridge() {
    document.addEventListener(
      'submit',
      function (e) {
        var form = e.target.closest('[data-pv-atc-form]');
        var scope = form && form.closest(SCOPE_SELECTOR);
        if (!scope) return; // not our instance - never touches the real PDP's own form

        e.preventDefault();
        e.stopPropagation();

        var root = form.closest('[data-pv-product]');
        var active =
          root.querySelector('[data-pv-variant-card].is-active') || root.querySelector('[data-pv-variant-card]');
        if (!active) return;

        var nameEl = active.querySelector('.pv-product__variant-name');
        var nameMatch = nameEl ? /(\d+)/.exec(nameEl.textContent) : null;
        var jarCount = nameMatch ? parseInt(nameMatch[1], 10) : 1;
        var sellingPlanId = active.getAttribute('data-selling-plan');
        var titleEl = root.querySelector('.pv-product__title');
        var saveEl = active.querySelector('.pv-product__variant-save');

        var app = window.PVQuizVikingApp;
        if (!app) {
          window.location.hash = '#step-15';
          return;
        }

        app.setMeta({
          selectedTier: jarCount,
          purchaseType: sellingPlanId ? 'subscription' : 'one_time',
          pvVariantId: active.getAttribute('data-variant-id'),
          pvSellingPlanId: sellingPlanId || null,
          pvProductTitle: titleEl ? titleEl.textContent.trim() : '',
          pvTierLabel: nameEl ? nameEl.textContent.trim() : '',
          pvPriceTotal: active.getAttribute('data-price-total'),
          pvCompareTotal: active.getAttribute('data-compare-total'),
          pvSaveText: saveEl ? saveEl.textContent.trim() : '',
          // Lets the add-on match this tier's delivery frequency - null for a
          // one-time purchase, since there's no recurring cadence to match.
          pvRefillDays: sellingPlanId ? active.getAttribute('data-refill-days') : null
        });
        app.trackEvent('quiz_offer_cta_click', { tier: jarCount });
        app.goToStep(15);
      },
      true
    );
  }

  function init() {
    initPersonalization();
    initPersonalizationOnStep14();
    initAddonCard();
    initSubmitBridge();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
